<template>
  <div class="admin-images">
    <!-- 頁首工具列 -->
    <header class="toolbar">
      <h1 class="title">後台｜圖片管理</h1>

      <div class="tools">
        <div class="search">
          <input
            v-model.trim="query"
            type="text"
            placeholder="搜尋檔名 / 標籤…"
            :disabled="loading"
            @input="onSearchInput"
          />
        </div>

        <div class="folder">
          <select v-model="folder" :disabled="loading" @change="refresh()">
            <option value="">全部資料夾</option>
            <option v-for="f in folders" :key="f" :value="f">{{ f }}</option>
          </select>
        </div>

        <label class="btn primary" :class="{ disabled: loading }">
          上傳圖片
          <input
            type="file"
            accept="image/*"
            multiple
            @change="onFileInput"
            :disabled="loading"
            hidden
          />
        </label>

        <button class="btn" @click="refresh" :disabled="loading">
          重新整理
        </button>

        <button class="btn cleanup" @click="cleanupStores" :disabled="loading || cleaning">
          {{ cleaning ? '清理中…' : '🗑️ 清理舊圖片' }}
        </button>
      </div>
    </header>

    <!-- 系統訊息 -->
    <transition name="fade">
      <div v-if="errorMessage" class="alert error">{{ errorMessage }}</div>
    </transition>
    <transition name="fade">
      <div v-if="successMessage" class="alert success">{{ successMessage }}</div>
    </transition>

    <!-- 拖放上傳區 -->
    <section
      class="dropzone"
      :class="{ 'is-dragover': dragover }"
      @dragover.prevent="dragover = true"
      @dragleave.prevent="dragover = false"
      @drop.prevent="onDrop"
    >
      <div>
        拖曳圖片到這裡上傳，或點擊「上傳圖片」按鈕
      </div>
      <small>支援多檔案。最大 {{ prettyMaxSize }} / 檔</small>
    </section>

    <!-- 清單 / 空狀態 -->
    <section v-if="!loading && images.length === 0" class="empty">
      <p>目前沒有符合條件的圖片。</p>
    </section>

    <!-- 圖片格狀列表 -->
    <section class="grid" v-if="images.length">
      <article v-for="img in images" :key="img.id" class="card">
        <div class="thumb" @click="openPreview(img)">
          <img :src="img.thumbUrl || img.url" :alt="img.name" />
        </div>
        <div class="meta">
          <div class="name" :title="img.name">{{ img.name }}</div>
          <div class="info">
            <span>{{ prettySize(img.size) }}</span>
            <span>·</span>
            <span>{{ (img.width && img.height) ? `${img.width}×${img.height}` : '—' }}</span>
          </div>
        </div>
        <div class="actions">
          <button class="btn sm" @click="copyUrl(img)">複製網址</button>
          <button class="btn sm warn" @click="deleteImage(img)" :disabled="deleting[img.id]">
            {{ deleting[img.id] ? '刪除中…' : '刪除' }}
          </button>
        </div>
      </article>
    </section>

    <!-- 分頁器 -->
    <footer class="pager" v-if="total > pageSize">
      <button class="btn" :disabled="page===1 || loading" @click="goto(page-1)">上一頁</button>
      <span class="page-text">第 {{ page }} / {{ totalPages }} 頁</span>
      <button class="btn" :disabled="page===totalPages || loading" @click="goto(page+1)">下一頁</button>
      <select v-model.number="pageSize" @change="goto(1)" :disabled="loading">
        <option :value="12">每頁 12</option>
        <option :value="24">每頁 24</option>
        <option :value="48">每頁 48</option>
      </select>
    </footer>

    <!-- 載入中 -->
    <div class="loading" v-if="loading">
      <div class="spinner" />
      <span>載入中…</span>
    </div>

    <!-- 大圖預覽 -->
    <div class="preview" v-if="preview" @click.self="preview=null">
      <div class="preview-inner">
        <img :src="preview.url" :alt="preview.name" />
        <div class="preview-bar">
          <span class="preview-name">{{ preview.name }}</span>
          <span class="flex-1" />
          <button class="btn sm" @click="copyUrl(preview)">複製網址</button>
          <button class="btn sm" @click="preview=null">關閉</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useAdminSysTempStore } from '@/store/adminSysTempStore.js'

// 靜態檔案（CDN）基底網址
const BASE_IMG_URL = 'https://img.jiliskin.com'

export default {
  name: 'AdminImages',
  data () {
    return {
      loading: false,
      errorMessage: '',
      successMessage: '',
      dragover: false,
      images: [],
      folders: [],
      folder: '',
      query: '',
      page: 1,
      pageSize: 24,
      total: 0,
      deleting: {},
      cleaning: false,
      preview: null,
      // 上傳限制（後端亦需驗證）
      maxFileSizeBytes: 8 * 1024 * 1024, // 8MB
      debounceTimer: null,
      // 轉檔設定
      enableWebp: true,
      webpQuality: 0.82
    }
  },
  async beforeCreate () {
    // 與 AdminLogin 相同：進頁前檢查是否登入，否則導回登入
    const useAdminSysTemp = useAdminSysTempStore()
    await useAdminSysTemp.fetchAdminCheckLogin()
    if (!useAdminSysTemp.isLoggedIn) {
      this.$router.push('/admin/login')
    }
  },
  created () {
    this.refresh()
  },
  computed: {
    totalPages () { return Math.max(1, Math.ceil(this.total / this.pageSize)) },
    prettyMaxSize () { return this.prettySize(this.maxFileSizeBytes) }
  },
  methods: {
    // 轉成公開可用網址（API 若回傳相對路徑 / 儲存鍵）
    toPublicUrl (u) {
      if (!u) return u
      // 已是 http(s) 直接回傳
      if (/^https?:\/\//i.test(u)) return u
      // 去掉開頭多餘的 /
      const path = String(u).replace(/^\/+/, '')
      return `${BASE_IMG_URL}/${path}`
    },
    normalizeItem (item) {
      const it = { ...item }
      it.url = this.toPublicUrl(it.url || it.path || it.key)
      it.thumbUrl = this.toPublicUrl(it.thumbUrl || it.thumb || '')
      return it
    },
    // ===== 基本資料流 =====
    async refresh () {
      this.loading = true
      this.errorMessage = ''
      try {
        const res = await axios.get('/api/admin/images', {
          params: { page: this.page, pageSize: this.pageSize, q: this.query || '', folder: this.folder || '', _t: Date.now() }
        })
        // 後端回傳格式建議：{ success, items:[{id,name,url,thumbUrl,size,width,height}], total, folders:[] }
        if (res.data && res.data.success) {
          const raw = res.data.items || []
          this.images = raw.map(this.normalizeItem)
          this.total = Number(res.data.total || this.images.length)
          this.folders = Array.isArray(res.data.folders) ? res.data.folders : this.folders
        } else {
          this.errorMessage = res.data?.message || '讀取失敗'
        }
      } catch (err) {
        this.errorMessage = err?.response?.data?.message || err?.message || '讀取失敗'
      } finally {
        this.loading = false
      }
    },
    goto (p) {
      this.page = Math.min(Math.max(1, p), this.totalPages)
      this.refresh()
    },

    onSearchInput () {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.page = 1
        this.refresh()
      }, 300)
    },

    // ===== 上傳 =====
    onFileInput (e) {
      const files = Array.from(e.target.files || [])
      if (files.length) this.upload(files)
      e.target.value = ''
    },
    onDrop (e) {
      this.dragover = false
      const files = Array.from(e.dataTransfer.files || [])
      const images = files.filter(f => /^image\//.test(f.type))
      if (images.length) this.upload(images)
    },
    async upload (files) {
      // 前端基本檢查
      const overs = files.filter(f => f.size > this.maxFileSizeBytes)
      if (overs.length) {
        this.errorMessage = `以下檔案超過大小限制(${this.prettyMaxSize})：` + overs.map(f => f.name).join(', ')
        return
      }

      // 若開啟 WebP，嘗試先在前端轉檔，以節省頻寬與後端負擔
      let convertedCount = 0
      if (this.enableWebp && typeof createImageBitmap === 'function') {
        const converted = []
        for (const f of files) {
          if (/^image\//.test(f.type) && f.type !== 'image/webp') {
            try {
              const webp = await this.transcodeToWebp(f, this.webpQuality)
              if (webp) {
                converted.push(webp)
                convertedCount++
                continue
              }
            } catch (e) { /* 失敗則回退原檔 */ }
          }
          converted.push(f)
        }
        files = converted
      }

      this.loading = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        const fd = new FormData()
        files.forEach(f => fd.append('files', f))
        if (this.folder) fd.append('folder', this.folder)
        fd.append('acceptWebp', '1')

        const res = await axios.post('/api/admin/images/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
        if (res.data?.success) {
          const tip = convertedCount ? `（其中 ${convertedCount} 檔已轉為 WebP）` : ''
          this.successMessage = `已上傳 ${files.length} 個檔案${tip}`
          this.page = 1
          await this.refresh()
        } else {
          this.errorMessage = res.data?.message || '上傳失敗'
        }
      } catch (err) {
        this.errorMessage = err?.response?.data?.message || err?.message || '上傳失敗'
      } finally {
        this.loading = false
      }
    },

    // 影像轉 WebP（最佳努力：支援現代瀏覽器；若失敗回傳 null）
    async transcodeToWebp (file, quality = 0.82) {
      if (!/^image\//.test(file.type) || file.type === 'image/webp') return null
      const bitmap = await createImageBitmap(file).catch(() => null)
      if (!bitmap) return null
      const canvas = document.createElement('canvas')
      canvas.width = bitmap.width
      canvas.height = bitmap.height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(bitmap, 0, 0)
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/webp', quality))
      if (!blob) return null
      const newName = file.name.replace(/\.[^.]+$/i, '') + '.webp'
      return new File([blob], newName, { type: 'image/webp', lastModified: Date.now() })
    },

    // ===== 操作 =====
    async deleteImage (img) {
      if (!confirm(`確定刪除「${img.name}」？`)) return
      this.$set ? this.$set(this.deleting, img.id, true) : (this.deleting = { ...this.deleting, [img.id]: true })
      try {
        // 從 img.path 取出資料夾（例如 stores/xxx.webp → stores）
        const imgFolder = img.path
          ? img.path.split('/').slice(0, -1).join('/')
          : (img.folder || '')
        const res = await axios.delete(
          `/api/admin/images/${encodeURIComponent(img.id)}`,
          { params: { folder: imgFolder } }
        )
        if (res.data?.success) {
          this.images = this.images.filter(x => x.id !== img.id)
          this.total = Math.max(0, this.total - 1)
          this.successMessage = '已刪除'
        } else {
          this.errorMessage = res.data?.message || '刪除失敗'
        }
      } catch (err) {
        this.errorMessage = err?.response?.data?.message || err?.message || '刪除失敗'
      } finally {
        this.$set ? this.$set(this.deleting, img.id, false) : (this.deleting = { ...this.deleting, [img.id]: false })
      }
    },
    async copyUrl (img) {
      try {
        const url = this.toPublicUrl(img.url || img.path || img.key)
        await navigator.clipboard.writeText(url)
        this.successMessage = '已複製圖片網址'
        setTimeout(() => { this.successMessage = '' }, 1500)
      } catch (e) {
        this.errorMessage = '複製失敗，請手動選取網址'
      }
    },
    openPreview (img) { this.preview = img },

    // ===== 清理孤兒圖片（兩步驟：先預覽，再確認刪除）=====
    async cleanupStores () {
      this.cleaning = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        // 步驟一：預覽（dryRun=true，不刪除）
        const preview = await axios.post('/api/admin/images/cleanup-stores', { dryRun: true })
        if (!preview.data?.success) {
          this.errorMessage = preview.data?.message || '預覽失敗'
          return
        }
        const files = preview.data.files || []
        if (files.length === 0) {
          this.successMessage = '沒有未使用的圖片，不需要清理'
          return
        }
        // 步驟二：顯示清單，讓使用者確認
        const list = files.slice(0, 20).join('\n') + (files.length > 20 ? `\n…共 ${files.length} 張` : '')
        const ok = confirm(`以下 ${files.length} 張圖片未被任何店家使用，確定刪除？\n\n${list}`)
        if (!ok) {
          this.successMessage = '已取消清理'
          return
        }
        // 步驟三：真正刪除（dryRun=false）
        const res = await axios.post('/api/admin/images/cleanup-stores', { dryRun: false })
        if (res.data?.success) {
          this.successMessage = res.data.message || '清理完成'
          if (res.data.deleted > 0) await this.refresh()
        } else {
          this.errorMessage = res.data?.message || '清理失敗'
        }
      } catch (err) {
        this.errorMessage = err?.response?.data?.message || err?.message || '清理失敗'
      } finally {
        this.cleaning = false
      }
    },

    // ===== 小工具 =====
    prettySize (bytes) {
      const KB = 1024, MB = KB * 1024, GB = MB * 1024
      if (bytes >= GB) return (bytes / GB).toFixed(2) + ' GB'
      if (bytes >= MB) return (bytes / MB).toFixed(2) + ' MB'
      if (bytes >= KB) return (bytes / KB).toFixed(2) + ' KB'
      return bytes + ' B'
    }
  }
}
</script>

<style scoped>
/* 深色系（依使用者偏好：黑底白字） */
:root { color-scheme: dark; }
.admin-images { min-height: 100vh; background: #0d1117; color: #e6edf3; }

.toolbar { display: flex; align-items: center; gap: 1rem; padding: 1rem; border-bottom: 1px solid #30363d; position: sticky; top: 0; background: rgba(13,17,23,.9); backdrop-filter: blur(6px); z-index: 2; }
.title { font-size: 1.25rem; margin: 0; }
.tools { display: flex; align-items: center; gap: .75rem; margin-left: auto; }
.search input, .folder select { background: #151b23; border: 1px solid #30363d; color: #e6edf3; padding: .5rem .6rem; border-radius: .5rem; }

.btn { background: #21262d; color: #e6edf3; border: 1px solid #30363d; padding: .5rem .8rem; border-radius: .6rem; cursor: pointer; transition: transform .05s ease, background .2s; }
.btn:hover { background: #30363d; }
.btn:disabled, .btn.disabled { opacity: .6; cursor: not-allowed; }
.btn.primary { background: #238636; border-color: #2ea043; }
.btn.primary:hover { background: #2ea043; }
.btn.warn { background: #8b0000; border-color: #a11; }
.btn.warn:hover { background: #a11; }
.btn.cleanup { background: #5a3e00; border-color: #a07000; color: #ffd966; }
.btn.cleanup:hover { background: #7a5500; }
.btn.sm { padding: .35rem .6rem; font-size: .9rem; }

.alert { margin: .75rem 1rem 0; padding: .6rem .8rem; border-radius: .5rem; border: 1px solid; }
.alert.error { background: #2a1416; border-color: #d14343; color: #ffb4b4; }
.alert.success { background: #112616; border-color: #2ea043; color: #a7f3c0; }
.fade-enter-active, .fade-leave-active { transition: opacity .2s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.dropzone { margin: 1rem; padding: 1.2rem; border: 2px dashed #30363d; border-radius: .75rem; display: grid; place-items: center; background: #0f141b; text-align: center; }
.dropzone.is-dragover { background: #0b1220; border-color: #2ea043; }
.dropzone small { color: #9da7b1; }

.grid { padding: 1rem; display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 1rem; }
.card { background: #0f141b; border: 1px solid #30363d; border-radius: .75rem; overflow: hidden; display: flex; flex-direction: column; }
.thumb { aspect-ratio: 1 / 1; background: #0b0f15; display: grid; place-items: center; cursor: zoom-in; }
.thumb img { max-width: 100%; max-height: 100%; object-fit: contain; }
.meta { padding: .6rem .75rem .2rem; }
.name { font-size: .95rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.info { color: #9da7b1; font-size: .85rem; display: flex; gap: .35rem; align-items: center; margin-top: .25rem; }
.actions { display: flex; gap: .5rem; padding: .6rem .75rem .8rem; margin-top: auto; }

.pager { display: flex; align-items: center; gap: .6rem; padding: 1rem; border-top: 1px solid #30363d; }
.page-text { color: #9da7b1; }

.loading { position: fixed; inset: 0; display: grid; place-items: center; background: rgba(0,0,0,.25); z-index: 5; backdrop-filter: blur(2px); }
.spinner { width: 28px; height: 28px; border: 3px solid #3a3f47; border-top-color: #2ea043; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: .5rem; }
@keyframes spin { to { transform: rotate(360deg); } }

/* 預覽 */
.preview { position: fixed; inset: 0; background: rgba(0,0,0,.7); display: grid; place-items: center; z-index: 10; }
.preview-inner { background: #0f141b; border: 1px solid #30363d; border-radius: .75rem; overflow: hidden; max-width: 90vw; max-height: 85vh; display: flex; flex-direction: column; }
.preview-inner img { max-width: 90vw; max-height: 75vh; object-fit: contain; background: #0b0f15; }
.preview-bar { display: flex; align-items: center; gap: .5rem; padding: .5rem .75rem; border-top: 1px solid #30363d; }
.preview-name { color: #9da7b1; }
.flex-1 { flex: 1; }

@media (max-width: 640px) {
  .grid { grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); }
}
</style>
